import { applyOpacity, periverseTheme, themeColors } from '@peritasai/ui-shared';
import type { SxProps, Theme } from '@mui/material/styles';

const scrollableAreaSx = {
  scrollbarGutter: 'stable',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '15px',
    backgroundColor: '#CFD8DC',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
} satisfies SxProps<Theme>;

export const discoveryPaperCardSx = {
  width: { xs: '680px', md: '100%' },
  maxWidth: { xs: 680, md: '100%' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'center', md: 'stretch' },
  gap: { xs: '16px', md: '16px' },
  borderRadius: '24px',
  border: `1px solid ${periverseTheme.palette.divider}`,
  background: periverseTheme.palette.background.paper,
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.08)',
  padding: { xs: '40px 32px', md: '16px' },
} satisfies SxProps<Theme>;

export const discoveryContainerSx = {
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: { xs: 'center', md: 'stretch' },
  flex: 1,
  height: '100%',
  minHeight: 0,
  alignSelf: 'stretch',
} satisfies SxProps<Theme>;

export const discoveryContentWrapSx = {
  flex: 1,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch',
} satisfies SxProps<Theme>;

export const discoveryHeaderWrapSx = {
  width: '100%',
  maxWidth: '100%',
  px: { xs: '16px', md: '0px' },
  pt: { xs: '16px', md: '0px' },
} satisfies SxProps<Theme>;

export const discoveryHeaderTitleStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: '1 1 0',
} satisfies SxProps<Theme>;

export const discoveryHeaderSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  gap: '4px',
} satisfies SxProps<Theme>;

export const discoveryCardWrapSx = {
  width: '100%',
  maxWidth: '100%',
  flex: 1,
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  px: { xs: '16px', md: '0px' },
  pt: 0,
} satisfies SxProps<Theme>;

export const discoveryTitleSx = {
  color: periverseTheme.palette.text.primary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '18px',
  fontWeight: 500,
  lineHeight: '32px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const discoverySubtitleSx = {
  color: periverseTheme.palette.text.secondary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const discoveryActionsSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
} satisfies SxProps<Theme>;

export const discoveryCancelButtonSx = {
  height: '44px',
  padding: '8px 22px',
  borderRadius: '8px',
  borderColor: applyOpacity(periverseTheme.palette.primary.light, 0.5),
  color: periverseTheme.palette.primary.main,
  textTransform: 'capitalize',
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const discoveryAssignButtonSx = {
  height: '44px',
  padding: '8px 22px',
  borderRadius: '8px',
  textTransform: 'capitalize',
  minWidth: '140px',
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const discoveryCardSx = {
  width: '100%',
  maxWidth: '100%',
  alignSelf: 'stretch',
  borderRadius: '24px',
  border: `1px solid ${periverseTheme.palette.divider}`,
  background: periverseTheme.palette.background.paper,
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.08)',
  padding: { xs: '16px', md: '24px' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0px',
  flex: 1,
  minHeight: 0,
  height: '100%',
} satisfies SxProps<Theme>;

export const discoveryPanelSx = {
  width: '100%',
  minWidth: '100%',
  alignSelf: 'stretch',
  flex: '1 0 0',
  borderRadius: '16px',
  border: `1px solid ${periverseTheme.palette.primary.main}`,
  background: themeColors.peritaspurple[50],
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '48px',
  padding: '0px',
  boxSizing: 'border-box',
  minHeight: 0,
} satisfies SxProps<Theme>;

export const discoveryCartIllustrationSx = {
  display: 'flex',
  width: '342px',
  height: '269.835px',
  padding: '1.734px 0.926px 0.055px 0',
  justifyContent: 'center',
  alignItems: 'center',
  aspectRatio: '109 / 86',
  objectFit: 'contain',
} satisfies SxProps<Theme>;

export const discoveryWaitingCopySx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
} satisfies SxProps<Theme>;

export const discoveryDetectedWrapSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  flex: 1,
  minHeight: 0,
} satisfies SxProps<Theme>;

export const discoveryDetectedListSx = {
  display: 'grid',
  gap: '12px',
  width: '100%',
  maxHeight: { xs: 'calc(100vh - 400px)', md: 'calc(100vh - 143px)' },
  overflowY: 'auto',
  pr: '4px',
  ...scrollableAreaSx,
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayRowSx = (selected: boolean) =>
  ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '12px 12px 12px 16px',
    minHeight: '48px',
    overflow: 'hidden',
    borderRadius: '16px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: selected ? '#F7F9FA' : '#FFFFFF',
    cursor: 'pointer',
  } satisfies SxProps<Theme>);

export const discoveryDetectedTrayBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '8px',
  backgroundColor: themeColors.peritaspurple[50],
  px: '8px',
  py: '3px',
  maxWidth: '100%',
  overflow: 'hidden',
} satisfies SxProps<Theme>;

export const discoverySuccessOuterSx = {
  flex: 1,
  width: '100%',
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} satisfies SxProps<Theme>;

export const discoverySuccessPanelSx = {
  backgroundColor: periverseTheme.palette.background.paper,
  borderRadius: '16px',
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.08)',
  padding: '40px 32px',
  minWidth: '320px',
  maxWidth: '360px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
} satisfies SxProps<Theme>;

export const discoverySuccessIconWrapSx = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: applyOpacity(periverseTheme.palette.success.dark, 0.2),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} satisfies SxProps<Theme>;

export const discoveryStatusTitleSx = {
  textAlign: 'center',
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '20px',
  fontWeight: 500,
  lineHeight: '26.68px',
  fontFeatureSettings: '"liga" off, "clig" off',
} satisfies SxProps<Theme>;

export const discoveryDetectedTitleSx = {
  ...discoveryStatusTitleSx,
  mt: '24px',
  mb: '16px',
  [periverseTheme.breakpoints.up('md')]: {
    mt: '0px',
    mb: '0px',
    textAlign: 'left',
    color: 'rgba(0, 0, 0, 0.87)',
    fontFamily: periverseTheme.typography.fontFamily,
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
} satisfies SxProps<Theme>;

export const discoveryStatusRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '40px',
  gap: '16px',
} satisfies SxProps<Theme>;

export const discoveryStatusLabelSx = {
  color: periverseTheme.palette.primary.main,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayNameSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20.02px',
  letterSpacing: '0.17px',
  fontFeatureSettings: '"liga" off, "clig" off',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  overflow: 'hidden',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayBadgeTextSx = {
  color: periverseTheme.palette.primary.dark,
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '18px',
  letterSpacing: '0.16px',
  fontFeatureSettings: '"liga" off, "clig" off',
  whiteSpace: 'nowrap',
} satisfies SxProps<Theme>;

export const discoveryCheckboxSx = {
  color: 'rgba(0, 0, 0, 0.60)',
  '&.Mui-checked': {
    color: periverseTheme.palette.primary.main,
  },
} satisfies SxProps<Theme>;

export const discoverySuccessTitleSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  textAlign: 'center',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '20px',
  fontWeight: 500,
  lineHeight: '26.68px',
  fontFeatureSettings: '"liga" off, "clig" off',
} satisfies SxProps<Theme>;

export const discoverySearchingLabelSx = {
  color: periverseTheme.palette.primary.dark,
  textAlign: 'center',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  fontFeatureSettings: '"liga" off, "clig" off',
} satisfies SxProps<Theme>;

export const hubStatusPaperCardSx = {
  textAlign: 'left',
  gap: 0,
  padding: '0px',
  width: '100%',
  maxWidth: '596px',
  alignItems: 'stretch',
} satisfies SxProps<Theme>;

export const hubStatusHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '16px',
  px: '24px',
  py: '16px',
  width: '100%',
} satisfies SxProps<Theme>;

export const hubStatusIconWrapSx = {
  width: '40px',
  height: '40px',
  borderRadius: '20px',
  backgroundColor: '#FFF2E0',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} satisfies SxProps<Theme>;

export const hubStatusTitleSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: '32px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const hubStatusBodySx = {
  px: '24px',
  py: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'flex-start',
  width: '100%',
} satisfies SxProps<Theme>;

export const hubStatusMessageSx = {
  color: 'rgba(0, 0, 0, 0.6)',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const hubStatusFooterSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  px: '24px',
  pb: '24px',
  width: '100%',
} satisfies SxProps<Theme>;
